import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { message, Spin, Select } from 'antd';
import { fetchSqlDataWithQuery, insertData, updateData } from '../../../services/api';
import type { CorFormGroup } from '../../../types';
import { useGetCorFormGroup } from '../../../hooks/genericHooks';
import GenericFormFill, { FORM_PRINT_CSS } from './GenericFormFill';
import FileUploadTool, { FileUploadToolHandle } from '../FileUploadTool';
import FileDownloadTool from '../FileDownloadTool'; // Re-import the FileDownloadTool component
import PrintButton from '../PrintButton';
import { decodeSvgFile } from '../PdfFormCreationFiles/svgFileCodec';

// Utility: get form definition table, data table, and fill title for a given route
function getFormInfoForPath(pathname: string): string[] {
  if (pathname.startsWith('/qaqc')) return ['qaqc_form_definitions', 'qaqc_form_records', 'QAQC Form Fill'];
  if (pathname.startsWith('/it')) return ['it_form_definitions', 'it_form_records', 'IT Form Fill'];
  if (pathname.startsWith('/to')) return ['to_form_definitions', 'to_form_records', 'TO Form Fill'];
  if (pathname.startsWith('/hse')) return ['hse_form_definitions', 'hse_form_records', 'HSE Form Fill'];
  if (pathname.startsWith('/admin')) return ['adm_form_definitions', 'adm_form_records', 'Admin Form Fill'];
  if (pathname.startsWith('/construction')) return ['const_form_definitions', 'const_form_records', 'Construction Form Fill'];
  if (pathname.startsWith('/material')) return ['mat_form_definitions', 'mat_form_records', 'Material Control Form Fill'];
  if (pathname.startsWith('/machinery')) return ['mach_form_definitions', 'mach_form_records', 'Machinery Form Fill'];
  if (pathname.startsWith('/dcc')) return ['dcc_form_definitions', 'dcc_form_records', 'DCC Form Fill'];
  if (pathname.startsWith('/official')) return ['off_form_definitions', 'off_form_records', 'Official Affairs Form Fill'];
  if (pathname.startsWith('/design')) return ['dsgn_form_definitions', 'dsgn_form_records', 'Design & Engineering Form Fill'];
  return ['', '', 'Form Fill'];
}

interface FormFillProps {
  formDefinition?: any;
  initialValues?: any;
  editMode?: boolean;
  isViewOrEditMode?: boolean; // New prop to explicitly control group selection visibility
}

const FormFill: React.FC<FormFillProps> = ({ 
  formDefinition, 
  initialValues, 
  editMode,
  isViewOrEditMode = false // Default to false for backward compatibility
}) => {
  const location = useLocation();
  const path = location.pathname;
  const [formDefTable, formDataTable, pageTitle] = getFormInfoForPath(path);

  const [formList, setFormList] = useState<any[]>([]);
  const [selectedFormId, setSelectedFormId] = useState<number | null>(null);
  const [selectedForm, setSelectedForm] = useState<any | null>(formDefinition || null);
  const [loading, setLoading] = useState(false);
  const [svgWithSize, setSvgWithSize] = useState<string>('');
  const [vbWidth, setVbWidth] = useState<number>(1000);
  const [vbHeight, setVbHeight] = useState<number>(1400);
  const [error, setError] = useState<string | null>(null);
  const [autoFormNum, setAutoFormNum] = useState<string>('');
  const [fieldValues, setFieldValues] = useState<Record<string, any>>(initialValues || { form_num: autoFormNum });
  const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({});
  const fileUploadRef = useRef<FileUploadToolHandle>(null);
  const printRootRef = useRef<HTMLDivElement>(null);
  const [groups, setGroups] = useState<CorFormGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const { data: allGroups } = useGetCorFormGroup();

  // Populate groups using generic hook, filtered by module route
  useEffect(() => {
    if (formDefinition) {
      // Skip group selection if we already have a form definition (edit/view mode)
      setSelectedGroupId(formDefinition.group_id || null);
      return;
    }
    
    const moduleRoute = '/' + path.split('/')[1];
    const src = (allGroups?.data as any[] | undefined) || [];
    const filtered = src.filter((g: any) => g?.route_name === moduleRoute);
    setGroups(filtered as CorFormGroup[]);
  }, [path, allGroups, formDefinition]);

  useEffect(() => {
    // Reflect group selection into field values
    setFieldValues(prev => ({ ...prev, group_id: selectedGroupId ?? null }));
  }, [selectedGroupId]);

  useEffect(() => {
    if (formDefinition) return;
    if (!formDefTable) {
      setError('No valid form definition table for this module.');
      return;
    }
    if (!selectedGroupId) {
      // Clear list until a group is selected
      setFormList([]);
      return;
    }
    setError(null);
    setLoading(true);
    const fetchFormList = async () => {
      try {
        const query = `SELECT id, form_description, form_ref, numbering_prefix FROM ${formDefTable} WHERE group_id = ${Number(selectedGroupId)}`;
        const data = await fetchSqlDataWithQuery(query);
        setFormList(Array.isArray(data) ? data : []);
      } catch (error) {
        message.error('Failed to fetch forms');
        setFormList([]);
      } finally {
        setLoading(false);
      }
    };
    fetchFormList();
  }, [formDefTable, formDefinition, selectedGroupId]);

  // Only fetch definition if not provided
  useEffect(() => {
    if (formDefinition) {
      // If we have a form definition (edit/view mode), use it directly
      setSelectedForm(formDefinition);
      setSelectedGroupId(formDefinition.group_id || null);
      return;
    }
    
    if (!formDefTable || !selectedFormId) return;
    setLoading(true);
    const query = `SELECT id, form_ref, form_description, numbering_prefix, form_json, svg_file, group_id FROM ${formDefTable} WHERE id = ${selectedFormId}`;
    fetchSqlDataWithQuery(query)
      .then(data => setSelectedForm(data?.[0] || null))
      .catch(() => message.error('Failed to fetch form details'))
      .finally(() => setLoading(false));
  }, [formDefTable, selectedFormId, formDefinition]);

  // SVG decode and viewbox
  useEffect(() => {
    const form = formDefinition || selectedForm;
    if (!form) {
      setSvgWithSize('');
      setVbWidth(1000);
      setVbHeight(1400);
      return;
    }
    let svgString = '';
    if (form.svg_file) {
      svgString = decodeSvgFile(form.svg_file);
    }
    const cleanedSvgString = svgString.replace(/<svg:/g, '<').replace(/<\/svg:/g, '</');
    // Parse form_json if backend returned it as a string
    let formJson: any = (form as any)?.form_json;
    if (typeof formJson === 'string') {
      try { formJson = JSON.parse(formJson); } catch { /* ignore */ }
    }
    let viewBox = [0, 0, 1000, 1400];
    const match = cleanedSvgString.match(/viewBox="([^"]+)"/);
    if (match) {
      viewBox = match[1].split(' ').map(Number);
    }
    const [vbX, vbY, vbW, vbH] = viewBox;
    setVbWidth(vbW);
    setVbHeight(vbH);
    const targetW = Number(formJson?.pdfWidth) || vbW;
    const targetH = Number(formJson?.pdfHeight) || vbH;
    let svgWithSz = cleanedSvgString;
    svgWithSz = svgWithSz.replace(/<svg([^>]*)>/,
      `<svg$1 width="${targetW}" height="${targetH}" viewBox="${vbX} ${vbY} ${vbW} ${vbH}">`);
    setSvgWithSize(svgWithSz);
  }, [formDefinition, selectedForm]);

  // Set initial field values if provided (for edit mode)
  useEffect(() => {
    if (initialValues && (formDefinition || selectedForm)) {
      let parsed;
      if (initialValues.fields) {
        if (typeof initialValues.fields === 'string') {
          try {
            parsed = JSON.parse(initialValues.fields);
          } catch {
            parsed = initialValues.fields; // fallback if not valid JSON
          }
        } else if (typeof initialValues.fields === 'object') {
          parsed = initialValues.fields;
        }
      }
      setFieldValues(parsed || initialValues);
    }
  }, [initialValues, formDefinition, selectedForm]);

  // Auto-generate form_num when selectedForm changes
  useEffect(() => {
    const generateFormNum = async () => {
      if (!selectedForm || !formDataTable || initialValues) {
        setAutoFormNum('');
        return;
      }
      const prefix = selectedForm.numbering_prefix || '';
      if (!prefix) {
        setAutoFormNum('');
        return;
      }
      try {
        // Query all form_num values with this prefix
        const query = `SELECT form_num FROM ${formDataTable} WHERE form_num LIKE '${prefix}%'`;
        const rows = await fetchSqlDataWithQuery(query);
        // Extract numeric suffixes
        const nums = rows
          .map((row: any) => row.form_num)
          .map((val: string) => {
            const match = val.match(/^(.*?)(\d{4})$/);
            return match ? parseInt(match[2], 10) : 0;
          });
        const maxNum = nums.length > 0 ? Math.max(...nums) : 0;
        const nextNum = maxNum + 1;
        const padded = nextNum.toString().padStart(4, '0');
        setAutoFormNum(`${prefix}${padded}`);
        setFieldValues({ form_num: `${prefix}${padded}` });
      } catch (err) {
        setAutoFormNum('');
      }
    };
    generateFormNum();
  }, [selectedForm, formDataTable, initialValues]);

  // Prepare overlay fields: use only the fields array from form_json
  const overlayFields = React.useMemo(() => {
    const raw = (selectedForm as any)?.form_json;
    let fj: any = raw;
    if (typeof fj === 'string') {
      try { fj = JSON.parse(fj); } catch { fj = null; }
    }
    return Array.isArray(fj?.fields) ? fj.fields : [];
  }, [selectedForm]);

  // Validation function for required fields
  const validateFields = () => {
    const errors: Record<string, boolean> = {};
    for (const field of overlayFields) {
      if (field.required && (!fieldValues[field.name] || String(fieldValues[field.name]).trim() === '')) {
        errors[field.name] = true;
      }
    }
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      message.error('Please fill all required fields before saving.');
    }
    return Object.keys(errors).length === 0;
  };

  // Handle save action for the form (was previously named submit)
  const handleSaveForm = async () => {
    if (!validateFields()) return;
    const handleGenericFormSave = async (allValues: Record<string, any>) => {
      if (!selectedForm || !formDataTable) return;
      setLoading(true);
      try {
        // Remove only form_record_id, keep everything else (including form_date, form_num)
        const { form_record_id, ...data } = allValues;
        let uploadPath: string | null = null;
        // Only finalize file uploads if there are files to upload
        if (fileUploadRef.current && data.form_num && fileUploadRef.current.hasFilesToUpload()) {
          const uploadResult = await fileUploadRef.current.finalizeUpload(data.form_num);
          if (!uploadResult.success) {
            message.error(uploadResult.error || 'File upload failed. Form not saved.');
            setLoading(false);
            return;
          }
          uploadPath = uploadResult.folderPath || null;
        }
        const payload = {
          form_ref: selectedForm.form_ref,
          form_description: selectedForm.form_description,
          form_date: data.form_date, // must be present in the form
          form_num: data.form_num,   // must be present in the form
          fields: JSON.stringify(data), // includes form_date and form_num as part of the JSON
          status: 'draft',
          upload_path: uploadPath,
        };
        // --- EDIT/CREATE LOGIC ---
        if (initialValues && initialValues.id) {
          // Edit mode: update existing record
          await updateData(formDataTable, initialValues.id, payload);
          message.success('Form entry updated!');
        } else {
          // Create mode: insert new record
          await insertData(formDataTable, payload);
          message.success('Form entry saved!');
        }
      } catch (err: any) {
        message.error(err?.message || 'Failed to save form entry');
      } finally {
        setLoading(false);
      }
    };
    handleGenericFormSave(fieldValues);
  };

  return (
    <div style={{ margin: '0 auto', padding: 24 }}>
      <h2 className="no-print">{pageTitle}</h2>
      {error && <div className="no-print" style={{ color: 'red', marginBottom: 16 }}>{error}</div>}
      <Spin spinning={loading}>
        <div className="no-print" style={{ marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center' }}>
          <Select
            style={{ width: 320 }}
            placeholder="Select Form Group"
            value={selectedGroupId ?? undefined}
            onChange={(val) => setSelectedGroupId(val)}
            allowClear
            options={groups.map(g => ({ value: g.id, label: g.group_name }))}
          />
          {formDefinition ? (
            <div>
              <h3>{formDefinition.form_description}</h3>
              <p>Form Ref: {formDefinition.form_ref}</p>
            </div>
          ) : (
            <Select
              showSearch
              style={{ width: 400 }}
              placeholder="Select a form to activate"
              optionFilterProp="children"
              disabled={!selectedGroupId}
              value={selectedFormId ?? undefined}
              onChange={id => setSelectedFormId(Number(id))}
              filterOption={(input, option) =>
                String(option?.children).toLowerCase().includes(input.toLowerCase())
              }
            >
              {formList.map(f => (
                <Select.Option key={f.id} value={f.id}>
                  {f.form_description} ({f.form_ref})
                </Select.Option>
              ))}
            </Select>
          )}
        </div>
        {(selectedGroupId && selectedForm && svgWithSize && (autoFormNum || editMode)) && (
          <GenericFormFill
            svg={svgWithSize}
            fields={overlayFields}
            formSpecificFields={{ form_num: editMode && initialValues?.form_num ? initialValues.form_num : autoFormNum }}
            formDefinition={selectedForm}
            fieldValues={fieldValues}
            setFieldValues={setFieldValues}
            validationErrors={validationErrors}
            loading={loading}
            onSave={() => {}}
            showPrintButton={false}
            printRootRef={printRootRef}
          />
        )}
        <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 16, marginTop: 32, paddingLeft: 0 }}>
          <FileUploadTool ref={fileUploadRef} buttonText="Upload Files" />
          {editMode && initialValues?.upload_path && initialValues.upload_path.trim() !== '' && (
            <FileDownloadTool contentPath={initialValues.upload_path} buttonText="Attached Files" />
          )}
          <button
            type="button"
            onClick={handleSaveForm}
            style={{ padding: '10px 48px', fontSize: 18, borderRadius: 8, background: '#1677ff', color: 'white', border: 'none', fontWeight: 600 }}
            disabled={loading}
          >
            Save Form
          </button>
          <PrintButton
            targetRef={printRootRef}
            tooltip="Print form"
            cloneRoot
            extraCss={FORM_PRINT_CSS}
          />
        </div>
      </Spin>
    </div>
  );
};

export default FormFill;
